import { useEffect, useRef } from 'react';
import { useOutboxStore } from '../store/outboxStore';
import { useAuthStore } from '../store/authStore';
import { subscribeOnline } from '../utils/network';
import { useVisibilityRefetch } from './useVisibilityRefetch';

/**
 * Bootstraps the durable offline outbox for observations (see docs/plans —
 * Durable Offline Outbox). Mount once at the app root, next to
 * registerServiceWorker in app/_layout.tsx.
 *
 * Hydrates the store from disk on mount, then wires every trigger that
 * should attempt a drain of the queue: coming back online, the app/tab
 * returning to the foreground, and signing into an account (the queue's
 * items are per-uid — see outboxStore). App start is covered by hydrate()
 * itself, which kicks off a drain once loaded.
 *
 * Store-only, per the component -> store -> api layering rule — this hook
 * never imports src/api/outbox directly. Every underlying call is a no-op
 * where the outbox itself is (native, OUTBOX_ENABLED off — see
 * src/constants/outbox.ts), so nothing here needs its own flag check.
 */
export function useOutboxSync(): void {
  useEffect(() => {
    void useOutboxStore.getState().hydrate();
  }, []);

  useEffect(() => {
    return subscribeOnline((online) => {
      if (online) useOutboxStore.getState().drain();
    });
  }, []);

  useVisibilityRefetch(() => {
    useOutboxStore.getState().drain();
  });

  // Drain on sign-in (covers items queued while signed out or under another
  // account, now eligible under the freshly signed-in uid). Compares
  // against the previous uid so sign-out (-> undefined) doesn't trigger it.
  const prevUidRef = useRef<string | undefined>(useAuthStore.getState().user?.id);
  useEffect(() => {
    return useAuthStore.subscribe((state) => {
      const uid = state.user?.id;
      if (uid && uid !== prevUidRef.current) {
        useOutboxStore.getState().drain();
      }
      prevUidRef.current = uid;
    });
  }, []);
}
